import {
  Dialog,
  DialogContent,
  Stack,
  Box,
  DialogContentText,
  Typography,
  DialogActions,
  Button,
  TextField,
  IconButton,
} from '@mui/material';
import { useRef, useState } from 'react';
import parse from 'html-react-parser';

type Props = {
  title?: string;
  desc?: string;
  open: boolean;
  showInput?: boolean;
  onCancel: () => void;
  onAgree: (code?: string) => void;
};

export default function ConfirmDialog(props: Props) {
  const { open, title, desc, showInput, onCancel, onAgree } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const [clicked, setClicked] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  return (
    <Dialog
      fullWidth
      open={open}
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: '350px',
          borderRadius: '13px',
        },
      }}
    >
      <DialogContent
        dividers
        sx={{
          p: 3,
          border: 'none',
          position: 'relative',
        }}
      >
        <Stack spacing={2} sx={{ textAlign: 'center' }}>
          <Box width="100%" textAlign="center">
            <svg
              width="40"
              height="39"
              viewBox="0 0 40 39"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="0.5" width="39" height="39" rx="7.8" fill="#F0A20A" />
              <path
                d="M21.5901 13.5908L21.2946 21.5908H18.7037L18.4083 13.5908H21.5901ZM19.9992 25.409C19.5598 25.409 19.1829 25.2556 18.8685 24.9488C18.5579 24.6382 18.4045 24.2613 18.4083 23.8181C18.4045 23.3863 18.5579 23.017 18.8685 22.7101C19.1829 22.4033 19.5598 22.2499 19.9992 22.2499C20.4158 22.2499 20.7833 22.4033 21.1015 22.7101C21.4234 23.017 21.5863 23.3863 21.5901 23.8181C21.5863 24.1135 21.5087 24.3825 21.3571 24.6249C21.2094 24.8635 21.0162 25.0548 20.7776 25.1988C20.539 25.3389 20.2795 25.409 19.9992 25.409Z"
                fill="white"
              />
            </svg>
          </Box>
          <DialogContentText component={Box}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: '#fff',
              }}
            >
              {title}
            </Typography>

            <Typography
              sx={{
                color: '#fff',
              }}
            >
              {parse(desc || '')}
            </Typography>
          </DialogContentText>
          {showInput && (
            <TextField
              inputRef={inputRef}
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="Password"
              autoComplete="current-password"
              sx={{
                '& .MuiFormLabel-root': {
                  lineHeight: 'normal',
                },
                '& .MuiInputBase-root': {
                  height: '50px',
                  color: 'white',
                },
                '& label.Mui-focused': {
                  color: 'white',
                },
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onAgree(inputRef.current?.value);
                }
              }}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    sx={{ color: 'white', fontSize: '16px' }}
                  >
                    <i className={showPassword ? 'fas fa-eye' : 'fas fa-eye-slash'} />
                  </IconButton>
                ),
              }}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack
          direction="row"
          spacing={2}
          width="100%"
          paddingX={2}
          paddingBottom={2}
          justifyContent="center"
        >
          {onCancel && (
            <Button
              fullWidth
              disabled={clicked}
              variant="outlined"
              color="info"
              onClick={onCancel}
              sx={{
                height: '46px',
                color: '#fff',
                borderColor: '#fff',
                '&:hover': {
                  borderColor: '#fff',
                },
              }}
            >
              Cancel
            </Button>
          )}
          <Button
            fullWidth
            disabled={clicked}
            variant="contained"
            color="info"
            onClick={() => {
              setClicked(true);
              onAgree(inputRef.current?.value);
            }}
            autoFocus
            sx={{
              height: '46px',
              color: '#000',
              background: '#fff',
              transition: 'all 0.3s ease',
              '&:hover': {
                opacity: 0.8,
                background: '#fff',
              },
            }}
          >
            Confirm
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
